import { createTheme } from "@mui/material/styles";
import { amber, blue, grey, green, purple, red } from "@mui/material/colors";

const YELLOW = {
	main: "#F0C400",
	hover: "#CCA700",
	press: "#997D00",
};

const SHADES = {
	100: "#E7E6E4",
	200: "#CECDCA",
	400: "#9D9A95",
	600: "#504E49",
	700: "#474541",
};

const BORDER_200 = "#CECDCA";
const BASE_DARK = "#190007"; // sidebar y “primary.dark”
//https://zenoo.github.io/mui-theme-creator/
//https://mui.com/material-ui/customization/default-theme/
const LightTheme = createTheme({
	palette: {
		background: {
			default: "#fff",
			paper: "#fff",
			gray: grey[50],
		},
		mode: "light",

		primary: {
			main: YELLOW.main,
			light: purple[50],
			dark: BASE_DARK, // ✅ agregado (primary dark = base dark)
			contrastText: BASE_DARK,
		},
		secondary: {
			main: grey[300],
			sub: grey[100],
		},
		success: {
			main: green[500],
			contrastText: "#fff",
		},
		warning: {
			main: amber[500],
			contrastText: "#fff",
		},
		error: {
			main: red[500],
		},
		info: {
			main: blue[500],
		},

		// ✅ para que el borde global sea el 200 de Figma
		divider: SHADES[200],
		text: {
			primary: BASE_DARK,
		},
	},

	typography: {
		// cambia a Inter si la estás usando
		fontFamily: `"Inter","Roboto","Helvetica","Arial",sans-serif`,

		// ✅ títulos: mayúscula 24px bold
		// h6: {
		// 	fontSize: "24px",
		// 	fontWeight: 700,
		// 	textTransform: "uppercase",
		// 	letterSpacing: "0.02em",
		// },
	},

	components: {
		/** -------------------------
		 *  CARDS: sin shadow + borde + radio 20
		 *  ------------------------- */
		MuiCard: {
			defaultProps: { elevation: 0 },
			styleOverrides: {
				root: ({ theme }) => ({
					boxShadow: "none",
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: 20,
				}),
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					boxShadow: "none",
				},
			},
		},

		/** -------------------------
		 *  MODAL / DIALOG: radio 20 + borde
		 *  (para Dialog y Papers de modales)
		 *  ------------------------- */
		MuiDialog: {
			styleOverrides: {
				paper: ({ theme }) => ({
					borderRadius: 20,
					border: `1px solid ${theme.palette.divider}`,
					boxShadow: "none",
				}),
			},
		},

		/** -------------------------
		 *  INPUTS: radio 16 + borde #CECDCA
		 *  TextField / Select / Autocomplete (Outlined)
		 *  ------------------------- */
		MuiOutlinedInput: {
			styleOverrides: {
				root: ({ theme }) => ({
					borderRadius: 16,
					"& .MuiOutlinedInput-notchedOutline": {
						borderColor: theme.palette.divider,
					},
					"&:hover .MuiOutlinedInput-notchedOutline": {
						borderColor: theme.palette.text.primary,
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: theme.palette.primary.main,
						borderWidth: 2,
					},
				}),
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: ({ theme }) => ({
					color: theme.palette.text.primary, // BASE_DARK
					"&.Mui-checked": {
						color: theme.palette.text.primary, // ✅ checked dark
					},
					"&.MuiCheckbox-indeterminate": {
						color: theme.palette.text.primary, // ✅ indeterminate dark
					},
				}),
			},
		},

		/** -------------------------
		 *  BUTTONS: estructura global
		 *  - NO rompe color por botón (primary/secondary/success/etc.)
		 *  - outlined cambia su borde/texto según color
		 *  - radio 16
		 *  ------------------------- */
		// En tu createTheme -> components -> MuiButton
		MuiButton: {
			defaultProps: {
				disableElevation: true,
			},
			styleOverrides: {
				root: ({ ownerState }) => ({
					borderRadius: 9999, // ✅ pill
					textTransform: "none",
					fontWeight: 600,
					// tamaños como en Figma (M y S)
					...(ownerState.size === "small"
						? { height: 36, padding: "6px 16px" }
						: { height: 44, padding: "10px 20px" }),
				}),

				/** PRIMARIO (AMARILLO) - contained */
				containedPrimary: {
					backgroundColor: YELLOW.main,
					color: BASE_DARK,
					border: "none",

					"&:hover": {
						backgroundColor: YELLOW.hover,
						border: "none",
					},
					"&:active": {
						backgroundColor: YELLOW.press, // press
					},

					"&.Mui-disabled": {
						backgroundColor: SHADES[200],
						color: SHADES[400],
					},

					// ✅ PRIMARIO DARK (solo cuando lo marcas)
					"&[data-tone='dark']": {
						backgroundColor: BASE_DARK,
						color: "#fff",
						"&:hover": { backgroundColor: SHADES[600] },
						"&:active": { backgroundColor: SHADES[700] },
						"&.Mui-disabled": {
							backgroundColor: SHADES[200],
							color: SHADES[400],
						},
					},
				},

				/** SECUNDARIO OUTLINE (borde dark, hover/press gris) */
				outlined: {
					borderWidth: "1px",
					borderColor: BASE_DARK,
					color: BASE_DARK,
					backgroundColor: "transparent",

					"&:hover": {
						borderColor: BASE_DARK,
						backgroundColor: SHADES[100],
					},
					"&:active": {
						backgroundColor: SHADES[200],
					},

					"&.Mui-disabled": {
						borderColor: SHADES[200],
						color: SHADES[400],
					},
				},

				/** GHOST (text) */
				text: {
					color: BASE_DARK,
					backgroundColor: "transparent",
					"&:hover": {
						backgroundColor: "transparent",
						color: SHADES[600],
					},
					"&:active": {
						color: SHADES[700],
					},
					"&.Mui-disabled": {
						color: SHADES[400],
					},
				},
			},
		},

		// ✅ LoadingButton (MUI Lab): estado loading como en Figma
		MuiLoadingButton: {
			styleOverrides: {
				root: {
					"&.MuiLoadingButton-loading": {
						backgroundColor: SHADES[200],
						color: SHADES[400],
					},
					"& .MuiLoadingButton-loadingIndicator": {
						color: SHADES[400],
					},
				},
			},
		},

		/** -------------------------
		 *  SIDEBAR (Drawer): usa Base dark
		 *  ------------------------- */
		MuiDrawer: {
			styleOverrides: {
				paper: ({ theme }) => ({
					backgroundColor: theme.palette.primary.dark, // 👈 Base dark
					color: "#fff",
					borderRight: `1px solid ${theme.palette.primary.dark}`,
				}),
			},
		},
		MuiListItemIcon: {
			styleOverrides: {
				root: { color: "inherit" },
			},
		},
	},
});

// const DarkTheme = createTheme({
// 	palette: {
// 		mode: "dark",
// 		background: {
// 			default: grey[900],
// 			paper: grey[800],
// 		},
// 		primary: {
// 			main: purple[300],
// 		},
// 		secondary: {
// 			main: grey[700],
// 			sub: grey[900],
// 		},
// 		success: {
// 			main: green[900],
// 			contrastText: "#fff",
// 		},
// 		warning: {
// 			main: amber[900],
// 			contrastText: "#fff",
// 		},
// 		error: {
// 			main: red[900],
// 		},
// 		info: {
// 			main: blue[900],
// 		},
// 	},
// });

// const CustomTheme = createTheme({
// 	palette: {
// 		background: {
// 			default: blueGrey[900],
// 			paper: "#000",
// 		},
// 		primary: {
// 			main: purple[900],
// 		},
// 		secondary: {
// 			main: brown[900],
// 		},
// 		success: {
// 			main: green[900],
// 			contrastText: "#fff",
// 		},
// 		warning: {
// 			main: amber[900],
// 			contrastText: "#fff",
// 		},
// 		error: {
// 			main: red[900],
// 		},
// 		info: {
// 			main: blue[900],
// 		},
// 	},
// });

export { LightTheme };
